'use client';

import { useEffect, useRef, useState, type CSSProperties } from 'react';
import styles from './reviews-carousel.module.css';
import type { Reference } from '@eliadmin/core/src/api/references/references-types';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import Image from '@eliadmin/core/src/components/image/image-wrapped';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { FaStar } from 'react-icons/fa';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';

interface InfiniteCarouselProps {
  reviews: Reference[];
  speed?: number;
}

export const ReviewsCarousel: React.FC<InfiniteCarouselProps> = ({ reviews, speed = 30 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = carouselRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setIsVisible(entry.isIntersecting));
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const duration = Number.isFinite(speed) && speed > 0 ? speed : 30;

  return (
    <div ref={carouselRef} className="relative z-10 w-full">
      <div
        className={styles.track}
        style={
          {
            animationDuration: `${duration}s`,
            '--reviews-play-state': isVisible ? 'running' : 'paused',
          } as CSSProperties
        }
      >
        {[0, 1].map(copy => (
          <div
            key={copy}
            className="flex shrink-0"
            aria-hidden={copy === 1 ? true : undefined}
            inert={copy === 1 ? true : undefined}
          >
            {reviews.map((review, index) => (
              <div key={`${review.id}-${index}`} className="mx-4 flex-shrink-0">
                <ReviewCard review={review} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

function ReviewCard({ review }: { review: Reference }) {
  const { t } = useTranslations();
  return (
    <div
      className={cn(
        'relative flex w-[260px] flex-col items-start justify-start gap-12 bg-[#131313] p-6 sm:w-[320px] md:w-[400px] md:p-8 lg:w-[600px]',
      )}
    >
      <Image
        src={'/images/icons/quote.svg'}
        alt="Google"
        width={0}
        height={0}
        className="absolute -top-3 right-6 z-[11] h-6 w-auto sm:-top-4 sm:h-8"
      />
      <Image
        src={'/images/google.svg'}
        alt="Google"
        width={0}
        height={0}
        className="h-6 w-auto sm:h-8"
      />
      <div className="flex w-full flex-col items-start justify-start gap-6">
        <HtmlRenderer
          html={review.description}
          className={cn(
            'line-clamp-3 h-[60px] w-full text-white sm:h-[72px]',
            'ea-review-card__description',
          )}
        />
        <div className="flex w-full flex-col items-start justify-start gap-4 md:flex-row md:items-end md:justify-between">
          <div className="flex flex-col items-start justify-start gap-1">
            <p className="text-base font-semibold text-white">
              {review.name} {review?.surname}
            </p>
            <span className="text-sm font-light text-white">{t('text2294')}</span>
          </div>
          <div className="flex items-center justify-start gap-2 md:justify-end">
            {[...Array(5)].map((_, i) => (
              <FaStar key={i} size={20} className="text-primary" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
